import React, { useState, useEffect, useRef } from 'react';
import { useQuery, useMutation } from 'react-apollo';
import {
  Card,
  Table,
  Button,
  Space,
  Tag,
  Popconfirm,
  message,
  Typography,
  Badge,
  Tooltip,
  Alert as AntAlert,
  Tabs,
  Alert,
} from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, HistoryOutlined } from '@ant-design/icons';
import moment from 'moment';
import AlertFormModal, { getPriorityLabel } from './AlertFormModal';
import GET_ALL_SYSTEM_ALERTS from '../../../core/GraphQl/Queries/GET_ALL_SYSTEM_ALERTS';
import GET_ALERT_HISTORY from '../../../core/GraphQl/Queries/GET_ALERT_HISTORY';
import CREATE_SYSTEM_ALERT from '../../../core/GraphQl/Mutations/CREATE_SYSTEM_ALERT';
import UPDATE_SYSTEM_ALERT from '../../../core/GraphQl/Mutations/UPDATE_SYSTEM_ALERT';
import DELETE_SYSTEM_ALERT from '../../../core/GraphQl/Mutations/DELETE_SYSTEM_ALERT';

const { Title, Paragraph } = Typography;
const { TabPane } = Tabs;

const AlertManagement = ({ currentUser }) => {
  // Track if component is mounted to prevent state updates after unmount
  const isMountedRef = useRef(true);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // All hooks must be called before any conditional returns
  const [modalVisible, setModalVisible] = useState(false);
  const [editingAlert, setEditingAlert] = useState(null);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10 });
  const [historyPagination, setHistoryPagination] = useState({ current: 1, pageSize: 10 });
  const [activeTab, setActiveTab] = useState('active');

  const { data, loading, refetch } = useQuery(GET_ALL_SYSTEM_ALERTS, {
    variables: {
      orderBy: 'createdAt_DESC',
    },
    fetchPolicy: 'network-only',
  });

  const { data: historyData, loading: historyLoading, refetch: refetchHistory } = useQuery(GET_ALERT_HISTORY, {
    variables: {
      orderBy: 'createdAt_DESC',
    },
    fetchPolicy: 'network-only',
    skip: activeTab !== 'history', // Only fetch when on history tab
  });

  const [createAlert, { loading: creating }] = useMutation(CREATE_SYSTEM_ALERT, {
    refetchQueries: [{ query: GET_ALL_SYSTEM_ALERTS, variables: { orderBy: 'createdAt_DESC' } }],
  });
  const [updateAlert, { loading: updating }] = useMutation(UPDATE_SYSTEM_ALERT, {
    refetchQueries: [{ query: GET_ALL_SYSTEM_ALERTS, variables: { orderBy: 'createdAt_DESC' } }],
  });
  const [deleteAlert, { loading: deleting }] = useMutation(DELETE_SYSTEM_ALERT, {
    refetchQueries: [{ query: GET_ALL_SYSTEM_ALERTS, variables: { orderBy: 'createdAt_DESC' } }],
  });

  // Check permission AFTER all hooks
  // TODO: Change to 'SYSTEM_ALERTS' once feature is added to database
  const hasPermission = currentUser?.features?.includes('ORGS');

  if (!hasPermission) {
    return (
      <Card style={{ maxWidth: 600, margin: '50px auto' }}>
        <AntAlert
          message="Access Denied"
          description="You do not have permission to access System Alerts management. This feature is only available to administrators."
          type="error"
          showIcon
        />
      </Card>
    );
  }

  const handleCreate = () => {
    setEditingAlert(null);
    setModalVisible(true);
  };

  const handleEdit = (record) => {
    setEditingAlert(record);
    setModalVisible(true);
  };

  const handleDelete = async (id) => {
    try {
      await deleteAlert({
        variables: {
          id,
          deletedBy: currentUser.id,
        },
      });
      if (isMountedRef.current) {
        message.success('Alert deleted successfully');

        // Calculate the new total count after deletion
        const newTotalCount = Math.max(0, totalCount - 1);
        const totalPages = Math.ceil(newTotalCount / pagination.pageSize);

        // If current page exceeds the new total pages, go to the last valid page
        const targetPage = pagination.current > totalPages ? Math.max(1, totalPages) : pagination.current;

        setPagination({ current: targetPage, pageSize: pagination.pageSize });
        refetch();
      }
    } catch (error) {
      if (isMountedRef.current) {
        message.error('Failed to delete alert: ' + error.message);
      }
    }
  };

  const handleSubmit = async (values) => {
    try {
      if (editingAlert) {
        await updateAlert({
          variables: {
            id: editingAlert.id,
            data: {
              ...values,
              updatedBy: { connect: { id: currentUser.id } },
            },
          },
        });
        if (isMountedRef.current) {
          message.success('Alert updated successfully');
        }
      } else {
        await createAlert({
          variables: {
            data: {
              ...values,
              createdBy: { connect: { id: currentUser.id } },
            },
          },
        });
        if (isMountedRef.current) {
          message.success('Alert created successfully');
        }
      }
      if (isMountedRef.current) {
        setModalVisible(false);
        setEditingAlert(null);
        refetch();
      }
    } catch (error) {
      if (isMountedRef.current) {
        message.error('Failed to save alert: ' + error.message);
      }
    }
  };

  const getAlertTypeTag = (type) => {
    const colors = {
      INFO: 'blue',
      WARNING: 'orange',
      ERROR: 'red',
      SUCCESS: 'green',
    };
    return <Tag color={colors[type]}>{type}</Tag>;
  };

  const getStatusBadge = (alert) => {
    const now = moment();
    const isActive = alert.active;
    const isWithinDateRange =
      now.isAfter(moment(alert.startDate)) && now.isBefore(moment(alert.endDate));

    if (!isActive) {
      return <Badge status="default" text="Inactive" />;
    }

    if (isWithinDateRange) {
      return <Badge status="success" text="Active & Visible" />;
    }

    if (now.isBefore(moment(alert.startDate))) {
      return <Badge status="processing" text="Scheduled" />;
    }

    return <Badge status="default" text="Expired" />;
  };

  const columns = [
    {
      title: 'Status',
      key: 'status',
      width: 150,
      responsive: ['sm'],
      render: (_, record) => getStatusBadge(record),
    },
    {
      title: 'Type',
      dataIndex: 'type',
      key: 'type',
      width: 100,
      render: (type) => getAlertTypeTag(type),
    },
    {
      title: 'Message',
      dataIndex: 'message',
      key: 'message',
      ellipsis: true,
      render: (text) => (
        <Tooltip title={text}>
          <span style={{ display: 'block', maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis' }}>{text}</span>
        </Tooltip>
      ),
    },
    {
      title: 'Date Range',
      key: 'dateRange',
      width: 200,
      responsive: ['lg'],
      render: (_, record) => (
        <div>
          <div style={{ fontSize: '12px' }}>
            <strong>Start:</strong> {moment(record.startDate).format('MMM D, YYYY h:mm A')}
          </div>
          <div style={{ fontSize: '12px' }}>
            <strong>End:</strong> {moment(record.endDate).format('MMM D, YYYY h:mm A')}
          </div>
        </div>
      ),
    },
    {
      title: 'Priority',
      dataIndex: 'priority',
      key: 'priority',
      width: 100,
      responsive: ['md'],
      sorter: (a, b) => a.priority - b.priority,
      render: (priority) => {
        const label = getPriorityLabel(priority);
        const colors = {
          LOW: 'blue',
          MEDIUM: 'orange',
          HIGH: 'red',
        };
        return <Tag color={colors[label]}>{label}</Tag>;
      },
    },
    {
      title: 'Dismissible',
      dataIndex: 'dismissible',
      key: 'dismissible',
      width: 100,
      responsive: ['md'],
      render: (dismissible) => (dismissible ? 'Yes' : 'No'),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 100,
      fixed: 'right',
      render: (_, record) => (
        <Space size="small">
          <Tooltip title="Edit">
            <Button
              type="link"
              icon={<EditOutlined />}
              onClick={() => handleEdit(record)}
              size="small"
            />
          </Tooltip>
          <Popconfirm
            title="Delete Alert"
            description="Are you sure you want to delete this alert?"
            onConfirm={() => handleDelete(record.id)}
            okText="Yes"
            cancelText="No"
          >
            <Tooltip title="Delete">
              <Button type="link" danger icon={<DeleteOutlined />} size="small" />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const handleTableChange = (newPagination) => {
    setPagination(newPagination);
  };

  const handleHistoryTableChange = (newPagination) => {
    setHistoryPagination(newPagination);
  };

  const getActionTag = (record) => {
    if (record.deleted) {
      return <Tag color="red">Deleted</Tag>;
    }
    if (record.updatedAt && record.updatedAt !== record.createdAt) {
      return <Tag color="orange">Updated</Tag>;
    }
    return <Tag color="green">Created</Tag>;
  };

  const historyColumns = [
    {
      title: 'Action',
      key: 'action',
      width: 100,
      responsive: ['sm'],
      render: (_, record) => getActionTag(record),
    },
    {
      title: 'Type',
      dataIndex: 'type',
      key: 'type',
      width: 100,
      render: (type) => getAlertTypeTag(type),
    },
    {
      title: 'Message',
      dataIndex: 'message',
      key: 'message',
      ellipsis: true,
      render: (text) => (
        <Tooltip title={text}>
          <span style={{ display: 'block', maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis' }}>{text}</span>
        </Tooltip>
      ),
    },
    {
      title: 'User',
      key: 'user',
      width: 120,
      responsive: ['md'],
      render: (_, record) => {
        if (record.deleted && record.deletedBy) {
          return `${record.deletedBy.name || record.deletedBy.username}`;
        }
        if (record.updatedBy && record.updatedAt !== record.createdAt) {
          return `${record.updatedBy.name || record.updatedBy.username}`;
        }
        return `${record.createdBy.name || record.createdBy.username}`;
      },
    },
    {
      title: 'Timestamp',
      key: 'timestamp',
      width: 160,
      responsive: ['lg'],
      render: (_, record) => {
        if (record.deleted && record.deletedAt) {
          return moment(record.deletedAt).format('MMM D, YYYY h:mm A');
        }
        if (record.updatedAt && record.updatedAt !== record.createdAt) {
          return moment(record.updatedAt).format('MMM D, YYYY h:mm A');
        }
        return moment(record.createdAt).format('MMM D, YYYY h:mm A');
      },
    },
  ];

  // Use real GraphQL data with client-side pagination
  const allAlerts = data?.systemAlerts || [];
  const totalCount = allAlerts.length;

  // Client-side pagination for active alerts
  const startIndex = (pagination.current - 1) * pagination.pageSize;
  const endIndex = startIndex + pagination.pageSize;
  const alerts = allAlerts.slice(startIndex, endIndex);

  const allHistoryAlerts = historyData?.systemAlerts || [];
  const historyTotalCount = allHistoryAlerts.length;

  // Client-side pagination for history
  const historyStartIndex = (historyPagination.current - 1) * historyPagination.pageSize;
  const historyEndIndex = historyStartIndex + historyPagination.pageSize;
  const historyAlerts = allHistoryAlerts.slice(historyStartIndex, historyEndIndex);

  return (
    <div style={{ padding: '24px' }}>
      <Card>
        <div style={{ marginBottom: '24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '16px' }}>
          <div>
            <Title level={2} style={{ marginBottom: '8px' }}>System Alert Management</Title>
            <Paragraph style={{ marginBottom: 0 }}>
              Create and manage system-wide alerts for scheduled maintenance, downtime
              notifications, and important announcements.
            </Paragraph>
          </div>
          <Button type="primary" icon={<PlusOutlined />} onClick={handleCreate} size="large">
            Create Alert
          </Button>
        </div>

        <Tabs activeKey={activeTab} onChange={setActiveTab}>
          <TabPane tab="Active Alerts" key="active">
            <Table
              columns={columns}
              dataSource={alerts}
              rowKey="id"
              loading={loading || deleting}
              pagination={{
                ...pagination,
                total: totalCount,
                showSizeChanger: true,
              }}
              onChange={handleTableChange}
              scroll={{ x: 800 }}
              size="small"
            />
          </TabPane>
          <TabPane tab={<span><HistoryOutlined /> History</span>} key="history">
            <Table
              columns={historyColumns}
              dataSource={historyAlerts}
              rowKey="id"
              loading={historyLoading}
              pagination={{
                ...historyPagination,
                total: historyTotalCount,
                showSizeChanger: true,
              }}
              onChange={handleHistoryTableChange}
              scroll={{ x: 800 }}
              size="small"
            />
          </TabPane>
        </Tabs>

        <AlertFormModal
          visible={modalVisible}
          onCancel={() => {
            setModalVisible(false);
            setEditingAlert(null);
          }}
          onSubmit={handleSubmit}
          initialValues={editingAlert}
          loading={creating || updating}
        />
      </Card>
    </div>
  );
};

export default AlertManagement;
